import { defineStore } from 'pinia'
import { Logo, Brand } from './LogoSelector/types'

interface LogoSearchState {
  searchQuery: string
  searchResults: Brand[]
  selectedBrand: Brand | null
  selectedLogo: Logo | null
  isSearching: boolean
  isLoadingBrand: boolean
  currentView: 'search' | 'brand-details'
}

/**
 * Store for managing logo search functionality
 */
export const useLogoSearchStore = defineStore('logoSearch', {
  state: (): LogoSearchState => ({
    searchQuery: '',
    searchResults: [],
    selectedBrand: null,
    selectedLogo: null,
    isSearching: false,
    isLoadingBrand: false,
    currentView: 'search',
  }),

  getters: {
    hasSelectedLogo: (state): boolean => {
      return state.selectedLogo !== null
    },

    isInBrandDetailsView: (state): boolean => {
      return state.currentView === 'brand-details'
    },

    isInSearchView: (state): boolean => {
      return state.currentView === 'search'
    },
  },

  actions: {
    /**
     * Set the search query
     * @param query
     */
    setSearchQuery(query: string) {
      this.searchQuery = query
      this.currentView = 'search'
    },

    /**
     * Set search results from API response
     * @param results
     */
    setSearchResults(results: Brand[]) {
      this.searchResults = results
    },

    /**
     * Set searching state
     * @param isSearching
     */
    setIsSearching(isSearching: boolean) {
      this.isSearching = isSearching
    },

    /**
     * Set selected brand with details
     * @param brand
     */
    setSelectedBrand(brand: Brand) {
      this.selectedBrand = brand
      this.currentView = 'brand-details'
      this.selectedLogo = null // Reset logo selection when switching brands
    },

    /**
     * Set loading brand state
     * @param isLoading
     */
    setIsLoadingBrand(isLoading: boolean) {
      this.isLoadingBrand = isLoading
    },

    setSelectedLogo(logo: Logo | null) {
      this.selectedLogo = logo
    },

    /**
     * Select or deselect a logo for preview
     * @param logo
     */
    selectLogo(logo: Logo): void {
      if (this.selectedLogo && this.selectedLogo.src === logo.src) {
        this.selectedLogo = null
      } else {
        this.selectedLogo = logo
      }
    },

    /**
     * Go back to search results view
     */
    goBackToSearch(): void {
      this.currentView = 'search'
      this.selectedBrand = null
      this.selectedLogo = null
    },

    /**
     * Reset all state to initial values
     */
    resetState(): void {
      this.searchQuery = ''
      this.searchResults = []
      this.selectedBrand = null
      this.selectedLogo = null
      this.isSearching = false
      this.isLoadingBrand = false
      this.currentView = 'search'
    },
  },
})
